import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, Users, X, Loader2, UserPlus, Wallet, KeyRound, StickyNote, Percent, Mail, Pencil, Trash2, ScanLine } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { DateTimeFields } from "./DateTimeFields";
import { PageHeader, EmptyState, StatusBadge, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [txnEdit, setTxnEdit] = useState(null);
  const [txnDel, setTxnDel] = useState(null);
  const [resetLink, setResetLink] = useState("");
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = (q = search, st = statusFilter) =>
    aapi.get("/users", { params: { search: q || undefined, status: st !== "all" ? st : undefined } })
      .then(({ data }) => setUsers(data)).catch(() => setUsers([]));

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    load(search, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openDetail = async (id) => {
    const { data } = await aapi.get(`/users/${id}`);
    setDetail({ ...data, balForm: { action: "add", amount: "", reference: "", note: "", date: "" }, newPw: "", emailEdit: data.user.email, notes: data.user.notes || "", discount: data.user.discount_pct || 0, mailForm: { template: "custom", subject: "", body: "", amount: "", refId: "", utr: "", simple: true }, mailBusy: false, lastAffidavit: null, ocrBusy: false, ocrResult: null });
    setTxnEdit(null);
    setTxnDel(null);
    setResetLink("");
  };

  const SAMPLE_AFFIDAVIT = (u) => [
    "REFUND AFFIDAVIT — FORMAL DECLARATION",
    "",
    `To, ${u.name} (${u.email})`,
    "",
    "Dear Sir/Madam,",
    "",
    "This is to formally declare and confirm that BEATKINGSMM has processed a refund in favour of the above-named customer, as per the details mutually agreed and recorded in our system.",
    "",
    "DETAILS:",
    "Refund Amount: ₹____",
    "Reference / Order ID: ____",
    "Original Payment Reference (UTR/Txn): ____",
    "Mode of Refund: Original payment method",
    "Processing Window: 0-45 working days as per our published Refund Policy",
    "",
    "This affidavit is issued as a true and formal record of the above refund transaction and may be produced for accounting, taxation or legal purposes, subject to applicable law.",
    "",
    "Declared and signed,",
    "For and on behalf of BEATKINGSMM",
  ].join("\n");

  const applyTemplate = (tpl) => {
    if (tpl === "affidavit") {
      setDetail({ ...detail, mailForm: { ...detail.mailForm, template: tpl, subject: "Refund Affidavit — BEATKINGSMM", body: "" } });
    } else {
      setDetail({ ...detail, mailForm: { ...detail.mailForm, template: tpl, subject: "", body: "" } });
    }
  };

  const setMailField = (k, v) => setDetail({ ...detail, mailForm: { ...detail.mailForm, [k]: v } });

  const makeResetLink = async () => {
    try {
      const { data } = await aapi.post(`/users/${detail.user.id}/reset-link`);
      setResetLink(data.reset_url);
      toast.success("Reset link ready — valid 24 hours", { description: "Share it manually (WhatsApp etc.). Nothing was emailed." });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const saveTxnDate = async () => {
    if (!txnEdit?.date) return;
    try {
      await aapi.put(`/transactions/${txnEdit.txn_id}`, { created_at: new Date(txnEdit.date).toISOString() });
      toast.success(`Transaction ${txnEdit.txn_id} re-dated — audit logged`);
      setTxnEdit(null);
      openDetail(detail.user.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteTxn = async (reverse) => {
    try {
      await aapi.delete(`/transactions/${txnDel.txn_id}?reverse=${reverse}`);
      toast.success(reverse ? "Transaction removed — wallet effect reversed" : "Transaction record removed");
      setTxnDel(null);
      openDetail(detail.user.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendMail = async () => {
    const f = detail.mailForm;
    if (!f.subject.trim() || !f.body.trim()) return toast.error("Subject and message are required");
    setDetail((d) => ({ ...d, mailBusy: true }));
    try {
      const { data } = await aapi.post(`/users/${detail.user.id}/email`, { subject: f.subject.trim(), body: f.body.trim(), simple: f.simple !== false });
      toast.success(`Email sent to ${data.sent_to}`, { description: f.simple !== false ? "Simple plain mail — no branding, audit logged." : "Delivered with your branding — audit logged." });
      setDetail((d) => ({ ...d, mailForm: { template: "custom", subject: "", body: "", simple: d.mailForm.simple !== false }, mailBusy: false }));
    } catch (e) {
      toast.error(formatApiError(e));
      setDetail((d) => ({ ...d, mailBusy: false }));
    }
  };

  const sendAffidavit = async () => {
    const f = detail.mailForm;
    if (f.body.trim().length < 20) return toast.error("Write the affidavit content first (min. 20 characters)");
    setDetail((d) => ({ ...d, mailBusy: true }));
    try {
      const payload = { content: f.body.trim(), ref_id: f.refId || "" };
      if (parseFloat(f.amount) > 0) payload.amount = parseFloat(f.amount);
      const { data } = await aapi.post(`/users/${detail.user.id}/affidavit`, payload);
      if (data.emailed) {
        toast.success(`Affidavit ${data.affidavit_id} emailed as a signed PDF`, {
          description: `Attached to the mail · verifiable at ${data.verify_url}`,
        });
      } else {
        toast.warning(`Affidavit ${data.affidavit_id} saved — email could not be delivered`, {
          description: "That address is unreachable. Download the PDF below and share it manually (WhatsApp etc.).",
        });
      }
      setDetail((d) => ({
        ...d,
        mailForm: { template: "custom", subject: "", body: "", amount: "", refId: "", utr: "" },
        mailBusy: false,
        lastAffidavit: { id: data.affidavit_id, file_url: data.file_url, verify_url: data.verify_url, emailed: data.emailed },
      }));
    } catch (e) {
      toast.error(formatApiError(e));
      setDetail((d) => ({ ...d, mailBusy: false }));
    }
  };

  const patchUser = async (updates, msg) => {
    try {
      await aapi.put(`/users/${detail.user.id}`, updates);
      toast.success(msg);
      const { data } = await aapi.get(`/users/${detail.user.id}`);
      setDetail((d) => ({ ...d, ...data, emailEdit: data.user.email }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveEmail = async () => {
    const em = (detail.emailEdit || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return toast.error("Valid email daalein");
    if (em === detail.user.email.toLowerCase()) return toast.error("Yehi email pehle se registered hai");
    if (!window.confirm(`Login email change karein?\n\n${detail.user.email}  →  ${em}\n\nUser ab sirf naye email se login kar payega. Action audit-log hoga.`)) return;
    await patchUser({ email: em }, `Login email updated → ${em}`);
  };

  const adjustBalance = async () => {
    const f = detail.balForm;
    if (!f.amount || parseFloat(f.amount) <= 0) return;
    if (f.action === "deduct" && !window.confirm(`Deduct ₹${f.amount} from ${detail.user.email}? This creates an audit record.`)) return;
    setBusy(true);
    try {
      const payload = { action: f.action, amount: parseFloat(f.amount), reference: f.reference, note: f.note };
      if (f.date) payload.created_at = new Date(f.date).toISOString();
      await aapi.post(`/users/${detail.user.id}/balance`, payload);
      toast.success(`Balance ${f.action === "add" ? "credited" : "deducted"} — audit logged`);
      const { data } = await aapi.get(`/users/${detail.user.id}`);
      setDetail((d) => ({ ...d, ...data, balForm: { action: "add", amount: "", reference: "", note: "", date: "" } }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (detail.newPw.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await aapi.post(`/users/${detail.user.id}/reset-password`, { new_password: detail.newPw });
      toast.success("Password reset — audit logged");
      setDetail((d) => ({ ...d, newPw: "" }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const extractScreenshot = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Image 8MB se choti honi chahiye");
    setDetail((d) => ({ ...d, ocrBusy: true, ocrResult: null }));
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await aapi.post("/payments/extract-screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDetail((d) => {
        const bal = { ...d.balForm };
        if (data.amount) bal.amount = String(data.amount);
        if (data.utr) bal.reference = data.utr;
        if (data.date) bal.date = `${data.date}T${data.time || "12:00"}`;
        if (data.payer_name || data.app) bal.note = [`Paid by ${data.payer_name || "customer"}`, data.app].filter(Boolean).join(" via ");
        return { ...d, balForm: bal, ocrBusy: false, ocrResult: data };
      });
      toast.success("Screenshot se details fetch ho gayi", { description: "Neeche fields auto-fill ho gaye — verify karke confirm karein." });
    } catch (err) {
      toast.error(formatApiError(err));
      setDetail((d) => ({ ...d, ocrBusy: false }));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await aapi.post("/users", createForm);
      toast.success("User created");
      setCreating(false);
      setCreateForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader title="Users" sub="Every customer, their wallet, their history.">
        <button
          onClick={() => setCreating(true)}
          data-testid="admin-add-user-button"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-5 py-2.5 text-xs font-bold text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)]"
        >
          <UserPlus size={14} /> Add User
        </button>
      </PageHeader>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className={`${inputCls} pl-11`}
            data-testid="admin-users-search"
          />
        </div>
        <div className="flex gap-2" data-testid="admin-users-status-filter">
          {[["all", "All"], ["active", "Active"], ["disabled", "Disabled"]].map(([v, label]) => (
            <button key={v} onClick={() => setStatusFilter(v)} data-testid={`users-filter-${v}`}
              className={`rounded-full border px-4 py-2.5 text-xs font-semibold transition-all duration-300 ${
                statusFilter === v
                  ? v === "disabled"
                    ? "border-rose-400/60 bg-rose-400/10 text-rose-300"
                    : "border-gold bg-gold/10 text-goldlight"
                  : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
              }`}>
              {label}{v === "disabled" && statusFilter === "disabled" && users ? ` (${users.length})` : ""}
            </button>
          ))}
        </div>
      </div>

      {users === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : users.length === 0 ? (
        <EmptyState icon={Users} title="No users found" sub="Try a different search." testid="admin-users-empty" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="overflow-hidden rounded-2xl border border-white/10" data-testid="admin-users-table">
          <div className="hidden grid-cols-12 gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500 md:grid">
            <span className="col-span-4">User</span>
            <span className="col-span-2">Balance</span>
            <span className="col-span-2">Orders</span>
            <span className="col-span-2">Status</span>
            <span className="col-span-2 text-right">Manage</span>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-row-${u.email}`}
              className="grid grid-cols-2 items-center gap-3 border-b border-white/5 bg-panel/80 px-5 py-4 transition-colors duration-300 last:border-0 hover:bg-gold/[0.04] md:grid-cols-12">
              <div className="col-span-2 md:col-span-4">
                <div className="text-sm font-medium text-neutral-100">{u.name}</div>
                <div className="text-xs text-neutral-500">{u.email}</div>
              </div>
              <span className="text-sm font-semibold text-goldlight md:col-span-2">{inr(u.balance)}</span>
              <span className="text-sm text-neutral-400 md:col-span-2">{u.orders_count} orders</span>
              <span className="md:col-span-2">
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                  (u.status || "active") === "active"
                    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                    : "border-rose-400/30 bg-rose-400/10 text-rose-300"
                }`}>{u.status || "active"}</span>
              </span>
              <div className="col-span-2 text-right md:col-span-2">
                <button
                  onClick={() => openDetail(u.id)}
                  data-testid={`admin-user-manage-${u.email}`}
                  className="rounded-full border border-gold/30 px-4 py-1.5 text-[11px] font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black"
                >
                  Manage
                </button>
              </div>
            </div>
          ))}
        </motion.div>
      )}

      <AnimatePresence>
        {creating && (
          <Modal onClose={() => setCreating(false)} testid="admin-create-user-modal">
            <h3 className="text-xl font-bold text-neutral-50">Add User</h3>
            <form onSubmit={createUser} className="mt-6 space-y-4">
              <input required minLength={2} placeholder="Full name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className={inputCls} data-testid="create-user-name" />
              <input required type="email" placeholder="Email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className={inputCls} data-testid="create-user-email" />
              <input required minLength={8} type="password" placeholder="Password (min. 8)" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className={inputCls} data-testid="create-user-password" />
              <button type="submit" disabled={busy} data-testid="create-user-submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold text-black disabled:opacity-50">
                {busy && <Loader2 size={15} className="animate-spin" />} CREATE USER
              </button>
            </form>
          </Modal>
        )}

        {detail && (
          <Modal onClose={() => setDetail(null)} wide testid="admin-user-detail-modal">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">USER PROFILE</div>
                <h3 className="mt-2 text-2xl font-bold text-neutral-50">{detail.user.name}</h3>
                <p className="text-sm text-neutral-400">{detail.user.email}</p>
                <p className="mt-1 font-mono text-[10px] text-neutral-600">
                  JOINED {fmtDate(detail.user.created_at)} · STATUS {(detail.user.status || "active").toUpperCase()}
                </p>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] tracking-[0.25em] text-neutral-500">BALANCE</div>
                <div className="mt-1 text-3xl font-bold text-goldlight" data-testid="admin-user-balance">{inr(detail.user.balance)}</div>
              </div>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <section className="rounded-2xl border border-gold/20 bg-gold/[0.04] p-5">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                  <Wallet size={14} className="text-gold" /> Add / Remove funds
                </h4>
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <select value={detail.balForm.action} onChange={(e) => setDetail({ ...detail, balForm: { ...detail.balForm, action: e.target.value } })} className={selectCls} data-testid="balance-action-select">
                      <option value="add">Add funds</option>
                      <option value="deduct">Remove funds</option>
                    </select>
                    <input type="number" placeholder="Amount ₹" value={detail.balForm.amount} onChange={(e) => setDetail({ ...detail, balForm: { ...detail.balForm, amount: e.target.value } })} className={inputCls} data-testid="balance-amount-input" />
                  </div>
                  <input placeholder="Payment / transaction reference" value={detail.balForm.reference} onChange={(e) => setDetail({ ...detail, balForm: { ...detail.balForm, reference: e.target.value } })} className={inputCls} data-testid="balance-reference-input" />
                  <div className="rounded-xl border border-dashed border-gold/30 bg-gold/[0.04] p-3" data-testid="ocr-upload-card">
                    <label className="flex cursor-pointer items-center justify-center gap-2 text-xs text-goldlight transition-colors hover:text-gold" data-testid="ocr-upload-button">
                      {detail.ocrBusy ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
                      {detail.ocrBusy ? "Screenshot read ho raha hai…" : "Payment screenshot upload karein — UTR · amount · date auto-fetch"}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={extractScreenshot} />
                    </label>
                    {detail.ocrResult && (
                      <p className="mt-2 text-center font-mono text-[9px] leading-relaxed tracking-[0.06em] text-emerald-300/90" data-testid="ocr-result">
                        FETCHED ✓ {[
                          detail.ocrResult.utr && `UTR ${detail.ocrResult.utr}`,
                          detail.ocrResult.amount && inr(detail.ocrResult.amount),
                          detail.ocrResult.date && `${detail.ocrResult.date}${detail.ocrResult.time ? " " + detail.ocrResult.time : ""}`,
                          detail.ocrResult.app,
                        ].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  <input placeholder="Note / reason" value={detail.balForm.note} onChange={(e) => setDetail({ ...detail, balForm: { ...detail.balForm, note: e.target.value } })} className={inputCls} data-testid="balance-note-input" />
                  <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-600">Date & time — leave empty for now, or backdate</label>
                    <div className="mt-1.5">
                      <DateTimeFields value={detail.balForm.date} onChange={(v) => setDetail({ ...detail, balForm: { ...detail.balForm, date: v } })} testid="balance-date" allowEmpty />
                    </div>
                  </div>
                  <button onClick={adjustBalance} disabled={busy} data-testid="balance-confirm-button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3 text-sm font-bold text-black disabled:opacity-50">
                    {busy && <Loader2 size={14} className="animate-spin" />}
                    CONFIRM — CREATES AUDIT RECORD
                  </button>
                </div>
              </section>

              <section className="space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                    <Percent size={14} className="text-gold" /> Custom discount %
                  </h4>
                  <div className="mt-3 flex gap-2">
                    <input type="number" min="0" max="90" value={detail.discount} onChange={(e) => setDetail({ ...detail, discount: e.target.value })} className={inputCls} data-testid="discount-input" />
                    <button onClick={() => patchUser({ discount_pct: parseFloat(detail.discount) || 0 }, "Discount updated")} data-testid="discount-save-button"
                      className="shrink-0 rounded-xl border border-gold/40 px-4 text-xs font-semibold text-goldlight hover:bg-gold hover:text-black">
                      Save
                    </button>
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                    <StickyNote size={14} className="text-gold" /> Internal notes
                  </h4>
                  <div className="mt-3 flex gap-2">
                    <input placeholder="Only admins see this" value={detail.notes} onChange={(e) => setDetail({ ...detail, notes: e.target.value })} className={inputCls} data-testid="notes-input" />
                    <button onClick={() => patchUser({ notes: detail.notes }, "Notes saved")} data-testid="notes-save-button"
                      className="shrink-0 rounded-xl border border-gold/40 px-4 text-xs font-semibold text-goldlight hover:bg-gold hover:text-black">
                      Save
                    </button>
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                    <Mail size={14} className="text-gold" /> Change login email
                  </h4>
                  <div className="mt-3 flex gap-2">
                    <input type="email" placeholder="New login email" value={detail.emailEdit} onChange={(e) => setDetail({ ...detail, emailEdit: e.target.value })} className={inputCls} data-testid="change-email-input" />
                    <button onClick={saveEmail} data-testid="change-email-save-button"
                      className="shrink-0 rounded-xl border border-gold/40 px-4 text-xs font-semibold text-goldlight hover:bg-gold hover:text-black">
                      Save
                    </button>
                  </div>
                  <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.08em] text-neutral-600">
                    User ab naye email se login karega — purana band. Duplicate email pe save nahi hoga · audit logged
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                    <KeyRound size={14} className="text-gold" /> Reset password / PIN
                  </h4>
                  <div className="mt-3 flex gap-2">
                    <input type="text" placeholder="New password (min. 8)" value={detail.newPw} onChange={(e) => setDetail({ ...detail, newPw: e.target.value })} className={inputCls} data-testid="reset-password-input" />
                    <button onClick={resetPassword} data-testid="reset-password-button"
                      className="shrink-0 rounded-xl border border-gold/40 px-4 text-xs font-semibold text-goldlight hover:bg-gold hover:text-black">
                      Reset
                    </button>
                  </div>
                  <button onClick={makeResetLink} data-testid="user-reset-link-button"
                    className="mt-3 w-full rounded-xl border border-white/10 py-2.5 text-[11px] font-medium text-neutral-400 transition-colors hover:border-gold/40 hover:text-goldlight">
                    Or get a shareable reset link (24h) — nothing is emailed
                  </button>
                  {resetLink && (
                    <div className="mt-2 flex gap-2">
                      <input readOnly value={resetLink} onFocus={(e) => e.target.select()} className={`${inputCls} font-mono text-[10px]`} data-testid="user-reset-link-output" />
                      <button onClick={() => { navigator.clipboard.writeText(resetLink); toast.success("Copied to clipboard"); }} data-testid="user-reset-link-copy"
                        className="shrink-0 rounded-xl border border-gold/40 px-3 text-[10px] font-semibold text-goldlight hover:bg-gold hover:text-black">
                        Copy
                      </button>
                    </div>
                  )}
                </div>
                <div className="rounded-2xl border border-gold/20 bg-gold/[0.04] p-5" data-testid="user-email-card">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                    <Mail size={14} className="text-gold" /> Email this user
                  </h4>
                  <div className="mt-3 space-y-3">
                    <select value={detail.mailForm.template} onChange={(e) => applyTemplate(e.target.value)} className={inputCls} data-testid="user-email-template">
                      <option value="custom">Custom message</option>
                      <option value="affidavit">Refund Affidavit — legal template</option>
                    </select>
                    {detail.mailForm.template === "affidavit" && (
                      <div className="space-y-3" data-testid="affidavit-fields">
                        <div className="flex gap-2">
                          <input placeholder="Refund amount ₹ (optional — records only)" value={detail.mailForm.amount} onChange={(e) => setMailField("amount", e.target.value)} className={inputCls} data-testid="affidavit-amount" />
                          <button type="button" onClick={() => setMailField("body", SAMPLE_AFFIDAVIT(detail.user))} data-testid="affidavit-sample-button"
                            className="shrink-0 rounded-xl border border-gold/40 px-4 text-[10px] font-semibold text-goldlight transition-colors hover:bg-gold hover:text-black">
                            INSERT SAMPLE
                          </button>
                        </div>
                        <p className="rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-2.5 text-[10px] leading-relaxed text-goldlight" data-testid="affidavit-note">
                          You write the entire affidavit below — every word you type becomes the signed PDF's content.
                          The logo, affidavit number, date, signature block, QR verification and legal footer are added
                          around it. Nothing is auto-written.
                        </p>
                      </div>
                    )}
                    {detail.mailForm.template !== "affidavit" && (
                      <label className="flex cursor-pointer items-center gap-2.5">
                        <input type="checkbox" checked={detail.mailForm.simple !== false} onChange={(e) => setMailField("simple", e.target.checked)}
                          className="h-4 w-4 accent-gold" data-testid="user-email-simple-toggle" />
                        <span className="text-[11px] text-neutral-400">Simple mail — no template, no branding, no document number</span>
                      </label>
                    )}
                    <input placeholder="Subject" value={detail.mailForm.subject} onChange={(e) => setMailField("subject", e.target.value)} className={inputCls} data-testid="user-email-subject" />
                    <textarea rows={detail.mailForm.template === "affidavit" ? 14 : 4}
                      placeholder={detail.mailForm.template === "affidavit"
                        ? "Write the complete affidavit here — every word goes into the signed PDF…"
                        : detail.mailForm.simple !== false
                          ? "Write your message — sent as a simple plain mail, no branding…"
                          : "Write your message — sent with BEATKINGSMM branding…"}
                      value={detail.mailForm.body} onChange={(e) => setMailField("body", e.target.value)} className={`${inputCls} resize-none font-mono text-xs`} data-testid="user-email-body" />
                    {detail.mailForm.template === "affidavit" && (
                      <p className="rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-2.5 text-[10px] leading-relaxed text-goldlight" data-testid="affidavit-note">
                        A signed affidavit PDF — site logo, affidavit number, QR verification and legal footer — is
                        generated automatically and attached to the email. The text below is a preview of its content.
                      </p>
                    )}
                    <button onClick={detail.mailForm.template === "affidavit" ? sendAffidavit : sendMail} disabled={detail.mailBusy} data-testid="user-email-send-button"
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3 text-xs font-bold tracking-[0.1em] text-black disabled:opacity-50">
                      {detail.mailBusy && <Loader2 size={14} className="animate-spin" />}
                      {detail.mailForm.template === "affidavit" ? "SEND AFFIDAVIT PDF BY EMAIL — AUDIT LOGGED" : "SEND EMAIL — MANUAL · AUDIT LOGGED"}
                    </button>
                    {detail.lastAffidavit && (
                      <div className={`rounded-xl border p-3.5 ${detail.lastAffidavit.emailed ? "border-emerald-400/30 bg-emerald-400/[0.07]" : "border-amber-400/30 bg-amber-400/[0.07]"}`} data-testid="affidavit-result">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`font-mono text-[11px] font-semibold ${detail.lastAffidavit.emailed ? "text-emerald-300" : "text-amber-300"}`}>
                            {detail.lastAffidavit.id}
                          </span>
                          <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] ${
                            detail.lastAffidavit.emailed
                              ? "border-emerald-400/30 text-emerald-300"
                              : "border-amber-400/40 text-amber-300"
                          }`} data-testid="affidavit-status">
                            {detail.lastAffidavit.emailed ? "EMAILED" : "SAVED — EMAIL UNDELIVERABLE"}
                          </span>
                        </div>
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          <a href={detail.lastAffidavit.file_url} target="_blank" rel="noreferrer" data-testid="affidavit-download-link"
                            className="rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark px-3.5 py-2 text-[10px] font-bold tracking-wide text-black">
                            DOWNLOAD PDF
                          </a>
                          <button onClick={() => { navigator.clipboard.writeText(detail.lastAffidavit.verify_url); toast.success("Verify link copied"); }} data-testid="affidavit-copy-link"
                            className="rounded-lg border border-white/15 px-3.5 py-2 text-[10px] font-semibold text-neutral-300 hover:border-gold/40 hover:text-goldlight">
                            COPY VERIFY LINK
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    const next = (detail.user.status || "active") === "active" ? "disabled" : "active";
                    if (next === "disabled" && !window.confirm(`Disable ${detail.user.email}? They will remain in the database.`)) return;
                    patchUser({ status: next }, `User ${next}`);
                  }}
                  data-testid="user-status-toggle"
                  className={`w-full rounded-xl border py-3 text-sm font-semibold transition-colors duration-300 ${
                    (detail.user.status || "active") === "active"
                      ? "border-rose-400/40 text-rose-300 hover:bg-rose-400/10"
                      : "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
                  }`}
                >
                  {(detail.user.status || "active") === "active" ? "Disable User" : "Re-enable User"}
                </button>
              </section>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              {[
                ["Recent orders", detail.orders.slice(0, 5).map((o) => `${o.order_id} · ${o.status} · ${inr(o.charge)}`)],
                ["Support tickets", detail.tickets.slice(0, 5).map((t) => `${t.ticket_id} · ${t.status} · ${t.subject}`)],
              ].map(([title, rows]) => (
                <section key={title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <h4 className="text-sm font-semibold text-neutral-100">{title}</h4>
                  <div className="mt-3 space-y-2">
                    {rows.length === 0 && <p className="text-xs text-neutral-600">None yet.</p>}
                    {rows.map((r, i) => (
                      <p key={i} className="truncate font-mono text-[11px] text-neutral-400">{r}</p>
                    ))}
                  </div>
                </section>
              ))}
              <section className="rounded-2xl border border-gold/20 bg-white/[0.02] p-5" data-testid="admin-user-txns-card">
                <h4 className="text-sm font-semibold text-neutral-100">Recent transactions</h4>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-600">Re-date or remove entries — audit logged</p>
                <div className="mt-3 space-y-2">
                  {detail.transactions.length === 0 && <p className="text-xs text-neutral-600">None yet.</p>}
                  {detail.transactions.slice(0, 6).map((t) => (
                    <div key={t.id} className="rounded-lg border border-white/5 px-2.5 py-2" data-testid={`admin-txn-row-${t.txn_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-mono text-[11px] text-neutral-400">
                          {t.txn_id} · <span className={t.type === "credit" ? "text-emerald-300" : "text-rose-300"}>{t.type} {inr(t.amount)}</span>
                        </span>
                        <span className="flex shrink-0 gap-1.5">
                          <button onClick={() => { setTxnDel(null); setTxnEdit({ txn_id: t.txn_id, date: toLocalInput(t.created_at) }); }}
                            data-testid={`admin-txn-edit-${t.txn_id}`} aria-label="Edit date"
                            className="flex h-6 w-6 items-center justify-center rounded-md border border-white/10 text-neutral-500 transition-colors hover:border-gold/50 hover:text-gold">
                            <Pencil size={11} />
                          </button>
                          <button onClick={() => { setTxnEdit(null); setTxnDel(t); }}
                            data-testid={`admin-txn-delete-${t.txn_id}`} aria-label="Remove"
                            className="flex h-6 w-6 items-center justify-center rounded-md border border-white/10 text-neutral-500 transition-colors hover:border-rose-400/50 hover:text-rose-300">
                            <Trash2 size={11} />
                          </button>
                        </span>
                      </div>
                      <div className="mt-0.5 font-mono text-[9px] text-neutral-600">{fmtDate(t.created_at)}</div>
                      {txnEdit?.txn_id === t.txn_id && (
                        <div className="mt-2">
                          <DateTimeFields value={txnEdit.date} onChange={(v) => setTxnEdit({ ...txnEdit, date: v })} testid={`admin-txn-date-${t.txn_id}`} />
                          <div className="mt-2 flex items-center gap-2">
                            <button onClick={saveTxnDate} data-testid={`admin-txn-date-save-${t.txn_id}`}
                              className="shrink-0 rounded-lg border border-gold/40 px-2.5 py-1.5 text-[10px] font-semibold text-goldlight hover:bg-gold hover:text-black">
                              Save date
                            </button>
                            <button onClick={() => setTxnEdit(null)} aria-label="Cancel"
                              className="shrink-0 rounded-lg border border-white/10 px-2 py-1.5 text-[10px] text-neutral-500 hover:text-neutral-200">
                              <X size={11} />
                            </button>
                          </div>
                        </div>
                      )}
                      {txnDel?.txn_id === t.txn_id && (
                        <div className="mt-2 rounded-lg border border-rose-400/30 bg-rose-400/10 p-2.5" data-testid={`admin-txn-confirm-${t.txn_id}`}>
                          <p className="text-[10px] leading-relaxed text-rose-200">Remove this record?</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <button onClick={() => deleteTxn(true)} data-testid={`admin-txn-delete-reverse-${t.txn_id}`}
                              className="rounded-lg border border-rose-400/40 px-2.5 py-1.5 text-[10px] font-semibold text-rose-200 hover:bg-rose-400/20">
                              Remove + reverse wallet
                            </button>
                            <button onClick={() => deleteTxn(false)} data-testid={`admin-txn-delete-keep-${t.txn_id}`}
                              className="rounded-lg border border-white/15 px-2.5 py-1.5 text-[10px] font-semibold text-neutral-300 hover:border-white/30">
                              Record only
                            </button>
                            <button onClick={() => setTxnDel(null)}
                              className="rounded-lg px-2 py-1.5 text-[10px] text-neutral-500 hover:text-neutral-200">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

const Modal = ({ children, onClose, testid, wide }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:items-center"
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 40, scale: 0.97 }}
      transition={{ duration: 0.5, ease: EASE }}
      className={`my-8 w-full ${wide ? "max-w-4xl" : "max-w-md"} rounded-3xl border border-gold/20 bg-panel p-6 sm:p-8`}
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
    >
      <div className="flex justify-end">
        <button onClick={onClose} data-testid={`${testid}-close`} aria-label="Close"
          className="-mr-2 -mt-2 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-colors hover:border-gold/50 hover:text-neutral-100">
          <X size={16} />
        </button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);

export { Modal, StatusBadge };
